import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DirhamIconColored } from '../components/DirhamIcon';
import CurrencyDisplay from '../components/CurrencyDisplay';
import { 
  FileText, TrendingUp, TrendingDown, Users, Home, 
  Send, Download, Calendar, CheckCircle, AlertTriangle,
  BarChart3
} from 'lucide-react';

const ComprehensiveReports = () => {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [report, setReport] = useState(null);
  const [periodType, setPeriodType] = useState('monthly');
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [quarter, setQuarter] = useState(Math.floor((new Date().getMonth()) / 3) + 1);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        period_type: periodType,
        year: year.toString()
      });
      
      if (periodType === 'monthly') {
        params.append('month', month.toString());
      } else if (periodType === 'quarterly') {
        params.append('quarter', quarter.toString());
      }
      
      const res = await api.get(`/reports/comprehensive?${params.toString()}`);
      setReport(res.data);
    } catch (error) {
      toast.error('فشل تحميل التقرير');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const sendReportEmail = async () => {
    setSending(true);
    try {
      const params = new URLSearchParams({
        period_type: periodType,
        year: year.toString()
      });
      
      if (periodType === 'monthly') {
        params.append('month', month.toString());
      } else if (periodType === 'quarterly') {
        params.append('quarter', quarter.toString());
      }
      
      const res = await api.post(`/reports/comprehensive/send-email?${params.toString()}`);
      if (res.data.success) {
        toast.success(res.data.message);
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      toast.error('فشل إرسال التقرير');
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    if (isAdmin()) {
      fetchReport();
    }
  }, [periodType, year, month, quarter]);

  if (!isAdmin()) {
    return (
      <div className="text-center py-12">
        <p className="text-xl text-muted-foreground">ليس لديك صلاحية للوصول إلى هذه الصفحة</p>
      </div>
    );
  }

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);
  const months = [
    { value: 1, label: 'يناير' },
    { value: 2, label: 'فبراير' },
    { value: 3, label: 'مارس' },
    { value: 4, label: 'أبريل' },
    { value: 5, label: 'مايو' },
    { value: 6, label: 'يونيو' },
    { value: 7, label: 'يوليو' },
    { value: 8, label: 'أغسطس' },
    { value: 9, label: 'سبتمبر' },
    { value: 10, label: 'أكتوبر' },
    { value: 11, label: 'نوفمبر' },
    { value: 12, label: 'ديسمبر' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <BarChart3 className="w-8 h-8 text-primary" />
            التقارير الشاملة
          </h1>
          <p className="text-muted-foreground">تقارير شهرية، ربع سنوية، نصف سنوية، وسنوية</p>
        </div>
        <Button 
          onClick={sendReportEmail} 
          disabled={sending || !report}
          className="bg-primary text-primary-foreground"
        >
          {sending ? (
            <>جاري الإرسال...</>
          ) : (
            <>
              <Send className="w-4 h-4 me-2" />
              إرسال عبر البريد
            </>
          )}
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-card border border-white/5 rounded-xl p-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">نوع التقرير</label>
            <Select value={periodType} onValueChange={setPeriodType}>
              <SelectTrigger className="bg-secondary/50 border-white/10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-white/10">
                <SelectItem value="monthly">شهري</SelectItem>
                <SelectItem value="quarterly">ربع سنوي</SelectItem>
                <SelectItem value="semi_annual">نصف سنوي</SelectItem>
                <SelectItem value="annual">سنوي</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">السنة</label>
            <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
              <SelectTrigger className="bg-secondary/50 border-white/10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-white/10">
                {years.map(y => (
                  <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {periodType === 'monthly' && (
            <div>
              <label className="block text-sm font-medium mb-2">الشهر</label>
              <Select value={month.toString()} onValueChange={(v) => setMonth(parseInt(v))}>
                <SelectTrigger className="bg-secondary/50 border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-white/10">
                  {months.map(m => (
                    <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          {periodType === 'quarterly' && (
            <div>
              <label className="block text-sm font-medium mb-2">الربع</label>
              <Select value={quarter.toString()} onValueChange={(v) => setQuarter(parseInt(v))}>
                <SelectTrigger className="bg-secondary/50 border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-white/10">
                  <SelectItem value="1">الربع الأول (يناير - مارس)</SelectItem>
                  <SelectItem value="2">الربع الثاني (أبريل - يونيو)</SelectItem>
                  <SelectItem value="3">الربع الثالث (يوليو - سبتمبر)</SelectItem>
                  <SelectItem value="4">الربع الرابع (أكتوبر - ديسمبر)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          
          <div className="flex items-end">
            <Button onClick={fetchReport} disabled={loading} variant="outline" className="w-full border-white/10">
              {loading ? 'جاري التحميل...' : 'تحديث'}
            </Button>
          </div>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      )}

      {report && !loading && (
        <>
          {/* Period Label */}
          <div className="bg-primary/20 border border-primary/50 rounded-xl p-4 text-center">
            <h2 className="text-2xl font-bold text-primary">{report.period_label}</h2>
            <p className="text-muted-foreground text-sm">
              {report.start_date?.split('T')[0]} إلى {report.end_date?.split('T')[0]}
            </p>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-card border border-white/5 rounded-xl p-6">
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <TrendingUp className="w-5 h-5 text-green-400" />
                <span>إيرادات الصالون</span>
              </div>
              <div className="text-3xl font-bold text-green-400">
                <CurrencyDisplay amount={report.salon_summary?.total_revenue} className="text-green-400" useLocale={true} />
              </div>
            </div>
            
            <div className="bg-card border border-white/5 rounded-xl p-6">
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <TrendingDown className="w-5 h-5 text-red-400" />
                <span>مصروفات الصالون</span>
              </div>
              <div className="text-3xl font-bold text-red-400">
                <CurrencyDisplay amount={report.salon_summary?.total_expenses} className="text-red-400" useLocale={true} />
              </div>
            </div>
            
            <div className="bg-card border border-white/5 rounded-xl p-6">
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <Users className="w-5 h-5" />
                <span>عدد العملاء</span>
              </div>
              <div className="text-3xl font-bold">
                {report.salon_summary?.total_clients?.toLocaleString()}
              </div>
            </div>
            
            <div className="bg-card border border-white/5 rounded-xl p-6">
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <DirhamIconColored className="w-5 h-5 text-primary" />
                <span>صافي ربح الصالون</span>
              </div>
              <div className={`text-3xl font-bold ${report.salon_summary?.net_profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                <CurrencyDisplay amount={report.salon_summary?.net_profit} className={report.salon_summary?.net_profit >= 0 ? 'text-green-400' : 'text-red-400'} useLocale={true} />
              </div>
            </div>
          </div>

          {/* Barber Performance */}
          <div className="bg-card border border-white/5 rounded-xl p-6">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              أداء الحلاقين
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-secondary/50 border-b border-white/5">
                  <tr>
                    <th className="px-4 py-3 text-start text-sm font-semibold">الحلاق</th>
                    <th className="px-4 py-3 text-start text-sm font-semibold">الإيرادات</th>
                    <th className="px-4 py-3 text-start text-sm font-semibold">العملاء</th>
                    <th className="px-4 py-3 text-start text-sm font-semibold">متوسط/عميل</th>
                  </tr>
                </thead>
                <tbody>
                  {report.barber_performance?.map((barber, idx) => (
                    <tr key={idx} className="border-b border-white/5 hover:bg-secondary/20">
                      <td className="px-4 py-3 font-medium">{barber.barber_name}</td>
                      <td className="px-4 py-3 text-green-400"><CurrencyDisplay amount={barber.total_revenue} className="text-green-400" useLocale={true} /></td>
                      <td className="px-4 py-3">{barber.total_clients}</td>
                      <td className="px-4 py-3 text-primary"><CurrencyDisplay amount={barber.avg_per_client} className="text-primary" useLocale={true} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Housing Summary */}
          <div className="bg-card border border-white/5 rounded-xl p-6">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Home className="w-5 h-5 text-primary" />
              ملخص سكن الموظفين
            </h3>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-secondary/30 rounded-lg p-4 text-center">
                <div className="text-sm text-muted-foreground">عدد السكنات</div>
                <div className="text-2xl font-bold">{report.housing_summary?.total_housings}</div>
              </div>
              <div className="bg-secondary/30 rounded-lg p-4 text-center">
                <div className="text-sm text-muted-foreground">إيرادات الفترة</div>
                <div className="text-2xl font-bold text-green-400"><CurrencyDisplay amount={report.housing_summary?.period_revenue} className="text-green-400" useLocale={true} /></div>
              </div>
              <div className="bg-secondary/30 rounded-lg p-4 text-center">
                <div className="text-sm text-muted-foreground">مصروفات الفترة</div>
                <div className="text-2xl font-bold text-red-400"><CurrencyDisplay amount={report.housing_summary?.period_expenses} className="text-red-400" useLocale={true} /></div>
              </div>
              <div className="bg-secondary/30 rounded-lg p-4 text-center">
                <div className="text-sm text-muted-foreground">صافي الربح</div>
                <div className={`text-2xl font-bold ${report.housing_summary?.period_profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  <CurrencyDisplay amount={report.housing_summary?.period_profit} className={report.housing_summary?.period_profit >= 0 ? 'text-green-400' : 'text-red-400'} useLocale={true} />
                </div>
              </div>
            </div>

            {/* Housing Details */}
            {report.housing_summary?.housings_details?.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-secondary/50 border-b border-white/5">
                    <tr>
                      <th className="px-4 py-3 text-start text-sm font-semibold">السكن</th>
                      <th className="px-4 py-3 text-start text-sm font-semibold">الإيرادات</th>
                      <th className="px-4 py-3 text-start text-sm font-semibold">المصروفات</th>
                      <th className="px-4 py-3 text-start text-sm font-semibold">الربح</th>
                      <th className="px-4 py-3 text-start text-sm font-semibold">الحالة</th>
                      <th className="px-4 py-3 text-start text-sm font-semibold">السكان</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.housing_summary.housings_details.map((housing, idx) => (
                      <tr key={idx} className="border-b border-white/5 hover:bg-secondary/20">
                        <td className="px-4 py-3 font-medium">{housing.name}</td>
                        <td className="px-4 py-3 text-green-400"><CurrencyDisplay amount={housing.period_revenue} className="text-green-400" useLocale={true} /></td>
                        <td className="px-4 py-3 text-red-400"><CurrencyDisplay amount={housing.period_expenses} className="text-red-400" useLocale={true} /></td>
                        <td className={`px-4 py-3 ${housing.period_profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          <CurrencyDisplay amount={housing.period_profit} className={housing.period_profit >= 0 ? 'text-green-400' : 'text-red-400'} useLocale={true} />
                        </td>
                        <td className="px-4 py-3">
                          {housing.is_profitable ? (
                            <span className="flex items-center gap-1 text-green-400">
                              <CheckCircle className="w-4 h-4" />
                              مربح
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-red-400">
                              <AlertTriangle className="w-4 h-4" />
                              خسارة
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">{housing.residents_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Profitable Housings (6+ months) */}
          {report.profitable_housings_6_months?.length > 0 && (
            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-6">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2 text-green-400">
                <CheckCircle className="w-5 h-5" />
                السكنات المربحة (6 شهور فأكثر)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {report.profitable_housings_6_months.map((housing, idx) => (
                  <div key={idx} className="bg-green-500/20 rounded-lg p-4">
                    <div className="font-bold text-green-400">{housing.name}</div>
                    <div className="text-sm text-muted-foreground">
                      {housing.profitable_months} شهر مربح
                    </div>
                    <div className="text-lg font-bold mt-2">
                      ربح شهري: <CurrencyDisplay amount={housing.monthly_profit} useLocale={true} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {report.profitable_housings_6_months?.length === 0 && report.housing_summary?.total_housings > 0 && (
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6 text-center">
              <AlertTriangle className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
              <p className="text-yellow-500">{report.profitable_housings_note}</p>
            </div>
          )}

          {/* Combined Total */}
          <div className="bg-primary/20 border border-primary/50 rounded-xl p-8 text-center">
            <h3 className="text-xl font-bold mb-4">الإجمالي الكلي (الصالون + السكنات)</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <div className="text-sm text-muted-foreground">إجمالي الإيرادات</div>
                <div className="text-2xl font-bold text-green-400">
                  <CurrencyDisplay amount={report.combined_summary?.total_revenue} className="text-green-400" useLocale={true} />
                </div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">إجمالي المصروفات</div>
                <div className="text-2xl font-bold text-red-400">
                  <CurrencyDisplay amount={report.combined_summary?.total_expenses} className="text-red-400" useLocale={true} />
                </div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">صافي الربح</div>
                <div className={`text-3xl font-bold ${report.combined_summary?.net_profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  <CurrencyDisplay amount={report.combined_summary?.net_profit} className={report.combined_summary?.net_profit >= 0 ? 'text-green-400' : 'text-red-400'} useLocale={true} />
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ComprehensiveReports;
